import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ArrowRightIcon } from 'lucide-react';
export function HeroSection() {
  return <section className="relative min-h-[100svh] flex flex-col justify-center items-center py-2 sm:py-8 px-3 sm:px-4 overflow-hidden bg-[#0B1A2E]" data-id="element-118">
      {/* Background glow effects */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-blue-600/15 rounded-full blur-[100px] pointer-events-none" data-id="element-119" />

      <div className="relative z-10 max-w-4xl mx-auto w-full flex flex-col items-center text-center gap-1.5 sm:gap-5" data-id="element-120">
        {/* Badge */}
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-121">
          <span className="inline-flex items-center gap-1.5 font-bold tracking-wider text-[9px] sm:text-xs uppercase px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full" style={{
          background: 'linear-gradient(135deg, rgba(202,166,74,0.12) 0%, rgba(184,134,11,0.08) 100%)',
          border: '1px solid rgba(202,166,74,0.45)',
          boxShadow: '0 0 12px rgba(202,166,74,0.15)'
        }} data-id="element-122">
            <span style={{
            background: 'linear-gradient(135deg, #f9e076, #caa64a, #b8860b)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }} data-id="element-123">
              Para contadores independientes que quieren organizar su trabajo
            </span>
          </span>
        </motion.div>

        {/* Title + Subtitle */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="space-y-1 sm:space-y-3" data-id="element-124">
          <h1 className="text-[17px] sm:text-4xl md:text-5xl font-extrabold text-white leading-[1.15] sm:leading-tight" data-id="element-125">
            El sistema completo para organizar tu estudio contable{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-200" data-id="element-126">
              de forma profesional
            </span>
          </h1>
          <p className="text-[10px] sm:text-base text-slate-300 max-w-2xl mx-auto leading-snug sm:leading-relaxed flex items-center justify-center flex-wrap gap-x-2 gap-y-0.5" data-id="element-127">
            <span data-id="element-128">Todo tu estudio contable organizado</span>
            <span className="text-slate-500 text-[8px] sm:text-[10px]" data-id="element-129">•</span>
            <span data-id="element-130">Sin desorden</span>
            <span className="text-slate-500 text-[8px] sm:text-[10px]" data-id="element-131">•</span>
            <span data-id="element-132">Sin atrasarte con clientes</span>
            <span className="text-slate-500 text-[8px] sm:text-[10px]" data-id="element-133">•</span>
            <span data-id="element-134">Sin improvisar</span>
          </p>
        </motion.div>

        {/* Product Image - Large & Prominent */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.92
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.2
      }} className="relative w-full max-w-[340px] sm:max-w-lg mx-auto my-0 sm:my-3" data-id="element-135">
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-136">
            <div className="w-64 h-40 rounded-full bg-blue-500/10 blur-[60px]" data-id="element-137" />
          </div>
          <img src="https://cdn.magicpatterns.com/uploads/2X953G6KX7DBQpjfMtnrcC/ChatGPT_Image_19_mar_2026,_11_50_38_p.m.-Photoroom.png" alt="Sistema de organización para contadores independientes" className="relative z-10 w-full h-auto object-contain" style={{
          filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.5))'
        }} data-id="element-138" />
        </motion.div>

        {/* Info Boxes - Always 2 columns */}
        <motion.div initial={{
        opacity: 0,
        y: 15
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.28
      }} className="w-full max-w-lg grid grid-cols-2 gap-2 sm:gap-3" data-id="element-139">
          {/* Left box - What's included */}
          <div className="rounded-xl p-2.5 sm:p-4 text-left" style={{
          background: 'linear-gradient(135deg, rgba(15,23,42,0.9) 0%, rgba(30,41,59,0.8) 100%)',
          border: '1px solid rgba(71,85,105,0.4)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2), 0 0 10px rgba(59,130,246,0.05)'
        }} data-id="element-140">
            <div className="flex items-center gap-1 mb-1.5 sm:mb-2" data-id="element-141">
              <span className="text-xs sm:text-sm" data-id="element-142">📦</span>
              <h3 className="text-[9px] sm:text-xs font-extrabold text-white uppercase tracking-wide" data-id="element-143">
                ¿Qué incluye el sistema?
              </h3>
            </div>
            <ul className="space-y-0.5 sm:space-y-1" data-id="element-144">
              {['+200 páginas', 'Plantillas listas', 'Checklists contables', 'Sistema de organización'].map((item, i) => <li key={i} className="flex items-start gap-1" data-id="element-145">
                  <CheckIcon className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 text-blue-400 mt-0.5 flex-shrink-0" data-id="element-146" />
                  <span className="text-[9px] sm:text-[11px] text-slate-300 font-medium leading-tight" data-id="element-147">
                    {item}
                  </span>
                </li>)}
            </ul>
          </div>

          {/* Right box - Premium bonuses */}
          <div className="rounded-xl p-2.5 sm:p-4 text-left" style={{
          background: 'linear-gradient(135deg, rgba(11,26,46,0.95) 0%, rgba(15,30,50,0.9) 100%)',
          border: '1px solid rgba(202,166,74,0.45)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.3), 0 0 15px rgba(241,196,15,0.1)'
        }} data-id="element-148">
            <div className="flex items-center justify-center gap-1 mb-1.5 sm:mb-2.5" data-id="element-149">
              <span className="text-xs sm:text-sm" data-id="element-150">🔥</span>
              <h3 className="text-[9px] sm:text-xs font-extrabold uppercase tracking-wider" style={{
              background: 'linear-gradient(135deg, #f1c40f, #f9e076, #caa64a)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }} data-id="element-151">
                Solo por hoy
              </h3>
            </div>
            <div className="space-y-1 sm:space-y-1.5" data-id="element-152">
              {[{
              tag: 'Más clientes',
              label: 'Sistema para generar clientes'
            }, {
              tag: 'Comunicación',
              label: 'Sistema para comunicarte con clientes'
            }].map((item, i) => <div key={i} className="flex items-center gap-1.5 rounded-lg px-2 py-1 sm:px-2.5 sm:py-1.5" style={{
              background: 'linear-gradient(135deg, rgba(202,166,74,0.12) 0%, rgba(241,196,15,0.06) 100%)',
              border: '1px solid rgba(202,166,74,0.25)'
            }} data-id="element-153">
                  <span className="text-[6px] sm:text-[8px] font-extrabold px-1 py-0.5 sm:px-1.5 rounded-full leading-none flex-shrink-0 uppercase tracking-wide whitespace-nowrap" style={{
                background: 'linear-gradient(135deg, #caa64a, #f1c40f)',
                color: '#3a2a08'
              }} data-id="element-154">
                    {item.tag}
                  </span>
                  <span className="text-[8px] sm:text-[11px] text-white font-semibold leading-tight" data-id="element-155">
                    {item.label}
                  </span>
                </div>)}
            </div>
          </div>
        </motion.div>

        {/* Price + CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.35
      }} className="w-full max-w-md flex flex-col items-center gap-1.5 sm:gap-3" data-id="element-156">
          {/* Price */}
          <div className="text-center" data-id="element-157">
            <span className="text-white/70 text-[9px] sm:text-xs font-bold uppercase tracking-wider" data-id="element-158">
              Precio especial hoy
            </span>
            <p className="text-white text-sm sm:text-xl font-extrabold mt-0.5" data-id="element-159">
              Todo por <span className="text-lg sm:text-3xl" data-id="element-160">$19.990 ARS</span>{' '}
              <span className="text-white/60 text-[10px] sm:text-sm font-medium" data-id="element-161">
                (pago único)
              </span>
            </p>
          </div>

          {/* CTA Button */}
          <motion.a href="#plan-selection" whileHover={{
          scale: 1.03
        }} whileTap={{
          scale: 0.97
        }} onClick={e => {
          e.preventDefault();
          document.getElementById('plan-selection')?.scrollIntoView({
            behavior: 'smooth'
          });
        }} className="w-full bg-[#22C55E] hover:bg-[#16a34a] text-white font-extrabold text-xs sm:text-lg py-3 sm:py-4 px-6 rounded-xl shadow-[0_0_25px_rgba(34,197,94,0.4)] hover:shadow-[0_0_35px_rgba(34,197,94,0.6)] transition-all duration-300 flex items-center justify-center gap-2 uppercase tracking-wide" data-id="element-162">
            Acceder al sistema completo
            <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-163" />
          </motion.a>

          {/* Trust badges */}
          <div className="flex items-center justify-center gap-2 sm:gap-4 text-[9px] sm:text-xs text-slate-400 font-medium flex-wrap" data-id="element-164">
            <span className="flex items-center gap-0.5" data-id="element-165">
              <CheckIcon className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 text-green-500" data-id="element-166" />{' '}
              Descarga inmediata
            </span>
            <span className="text-slate-600" data-id="element-167">•</span>
            <span className="flex items-center gap-0.5" data-id="element-168">
              <CheckIcon className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 text-green-500" data-id="element-169" />{' '}
              Acceso digital
            </span>
            <span className="text-slate-600" data-id="element-170">•</span>
            <span className="flex items-center gap-0.5" data-id="element-171">
              <CheckIcon className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 text-green-500" data-id="element-172" />{' '}
              Garantía 7 días
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}